"use client";

import { useTranslations } from "next-intl";
import type * as React from "react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { OwnedWrapper } from "@/hooks/stake/useUserWrappers";

interface WrapperSelectProps {
  wrappers: OwnedWrapper[];
  selectedWrapId: bigint | null;
  onSelect: (wrapId: bigint) => void;
  isLoading?: boolean;
}

export const WrapperSelect: React.FC<WrapperSelectProps> = ({
  wrappers,
  selectedWrapId,
  onSelect,
  isLoading = false,
}) => {
  const t = useTranslations("stake");
  return (
    <Select
      // modal={false} keeps the trigger interactive when nested inside a
      // Radix Dialog / vaul Drawer (outer overlay's focus-trap otherwise
      // intercepts pointer events before Select opens).
      modal={false}
      dir="rtl"
      // Trigger stays enabled during load — empty state renders inside the
      // open dropdown. Disabling the trigger silently traps clicks.
      value={selectedWrapId?.toString() ?? ""}
      onValueChange={(v) => onSelect(BigInt(v))}
    >
      <SelectTrigger className="w-full h-10 bg-transparent border-stroke-light text-text-secondary data-[state=open]:border-stroke-primary">
        <SelectValue
          placeholder={
            isLoading ? t("modal.loading") : t("modal.selectWrapperPlaceholder")
          }
        />
      </SelectTrigger>
      <SelectContent
        dir="rtl"
        // z-[120] lifts the popup above vaul Drawer overlay/content (z-110)
        // and Radix Dialog overlay (z-50). Matches ConvertDialog RHFSelect.
        // w-[var(--radix-select-trigger-width)] pins the popup to the
        // trigger's own width so it never grows past the select input.
        className="z-[120] w-[var(--radix-select-trigger-width)] rounded-md bg-bg-elevated border-stroke-light p-0 max-h-64"
        sideOffset={4}
      >
        {wrappers.length === 0 ? (
          <div className="px-3 py-4 text-xs text-text-secondary text-center">
            {isLoading ? t("modal.loading") : t("modal.noWrappersInWallet")}
          </div>
        ) : (
          wrappers.map((w) => (
            <SelectItem
              key={w.wrap_id.toString()}
              value={w.wrap_id.toString()}
              // py-3 + px-4 for Figma-matching row height + breathing room.
              // [&>span:last-child] forces the children container to fill
              // the row width and justify-between across the 3 fields.
              className="text-text-primary !pe-2 !ps-2 !py-1 data-[highlighted]:bg-bg-tertiary/60 focus:bg-bg-tertiary/60 [&>span:last-child]:w-full [&>span:last-child]:justify-between"
            >
              <span className="flex items-center gap-3 text-sm w-full">
                <span>
                  {t("modal.wrapperPlanLabel", { plan: w.plan_type })}
                </span>
                <span
                  aria-hidden
                  className="inline-block w-1.5 h-1.5 bg-text-disabled shrink-0"
                />
                <span>
                  {t("modal.wrapperIdLabel", { id: w.wrap_id.toString() })}
                </span>
                <span
                  aria-hidden
                  className="inline-block w-1.5 h-1.5 bg-text-disabled shrink-0"
                />
                <span>
                  {t("modal.wrapperNftIdLabel", { id: w.token_id.toString() })}
                </span>
              </span>
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
};
